import React, { useEffect, useRef, useState } from "react";
import { periodLabel, metricValue, metricUnit } from "./reportsFormat.mjs";
import ReportsChart, { chartFacts } from "./ReportsChart";

/*
 * THE DRILL-DOWN — the big chart under the grid, showing whichever stat was
 * clicked last.
 *
 * Gabriel, 2026-10-07: "Allow me to change the chart by clicking on ANY stat —
 * currently it's just certain ones. This should be conversion rates too." So it
 * now opens from every tile, every funnel stage and every funnel rate, and it
 * follows the page's date range: the bars are the scope cut into weeks or
 * months, which is why it no longer fetches anything. Both cuts arrive on the
 * tile.
 *
 * The partial period is hatched and kept out of the average, except the one
 * running now, which shows what has happened in yellow and where it is heading
 * in grey (see ReportsChart).
 */

export default function ReportsDrill({ tile, onSelectPeriod, onShowMembers, onClose }) {
  const [granularity, setGranularity] = useState(tile.default_granularity || "monthly");
  // A different stat resets to that stat's own default cut.
  useEffect(() => setGranularity(tile.default_granularity || "monthly"), [tile.id, tile.default_granularity]);

  // Opened from the funnel, the chart can sit below the fold; bring it up.
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [tile.id]);

  const { reference, referenceLabel, current } = chartFacts(tile, granularity);
  const unit = metricUnit(tile.format);

  return (
    <section className="crm-drill" ref={ref}>
      <div className="crm-drill__head">
        <div>
          <div className="crm-drill__eyebrow">
            {tile.label} · {granularity === "weekly" ? "weekly" : "monthly"}
          </div>
          <div className="crm-drill__valrow">
            <span className="crm-drill__value crm-num">{metricValue(tile.value, tile.format)}</span>
            {unit && tile.value != null && <span className="crm-tile__unit">{unit}</span>}
            <span className="crm-drill__caption">
              {tile.qualifier || ""}
              {reference != null && tile.has_members ? ` · ${referenceLabel.replace("avg", `${granularity} average`)}` : ""}
              {current && current.projection
                ? ` · ${periodLabel(current.key)} heading for ${current.projection.projected}`
                : ""}
            </span>
          </div>
        </div>

        <div className="crm-drill__controls">
          {tile.has_members && onShowMembers && (
            <button type="button" className="crm-drill__collapse" onClick={() => onShowMembers(tile.id)}>
              Companies
            </button>
          )}
          <span className="crm-seg crm-seg--scope" role="group" aria-label="Chart granularity">
            {["weekly", "monthly"].map((g) => (
              <button
                key={g}
                type="button"
                className={granularity === g ? "sel" : ""}
                aria-pressed={granularity === g}
                onClick={() => setGranularity(g)}
              >
                {g === "weekly" ? "Weekly" : "Monthly"}
              </button>
            ))}
          </span>
          <button type="button" className="crm-drill__collapse" onClick={onClose}>
            Collapse
          </button>
        </div>
      </div>

      <ReportsChart tile={tile} granularity={granularity} onSelectPeriod={onSelectPeriod} />

      <div className="crm-drill__foot">
        <span className="crm-drill__legend">
          <span className="crm-drill__swatch" />
          Partial {granularity === "weekly" ? "week" : "month"}, out of the average
        </span>
        {current && current.projection && (
          <span className="crm-drill__legend" title={current.projection.basis}>
            <span className="crm-drill__swatch crm-drill__swatch--proj" />
            Projected: last 4 weeks' pace, seasonally adjusted
          </span>
        )}
        <span className="crm-drill__note">
          {tile.has_members ? "click a bar to see the companies behind it · an event counts in the period it happened" : ""}
          {tile.series_note || ""}
          {!tile.has_members && !tile.series_note && tile.format === "percent"
            ? "each bar is the cohort that entered in that period"
            : ""}
          {!tile.has_members && !tile.series_note && tile.format === "days"
            ? "each bar is the median for companies that entered in that period"
            : ""}
        </span>
      </div>
    </section>
  );
}
